import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PlugIcon } from '../../../assets/icons/plug-icon.svg';
import { useTariff } from '../../../context/TariffContext';
import { EstimateApiResponse, EstimateInterface } from '../../../interface/charger.interface';
import MyButton from '../../../shared-components/button/button';
import { getLocalStorageItem } from '../../../shared-functions/LocalStorage';
import charger from '../../../utils/api/charger';

import TariffFeesOverview from './TariffFeesOverview';

interface ConnectorInterface {
  _id: string;
  connector: string;
  connectorId: string | number;
  isReservationEnabled: boolean;
  status: string;
  capacity?: string;
  currentType?: string;
  currentForm?: string;
}

export type LimitTab = 'amount' | 'energy' | 'time' | 'battery';

export type KeypadTarget =
  | 'amount'
  | 'energy'
  | 'time'
  | 'battery'
  | 'currentSoc'
  | 'batteryCapacity';

export interface ChargeLimitConfig {
  type: LimitTab;
  value: string;
  currentSoc?: string | number;
  batteryCapacityKwh?: string | number;
}

interface FeesInterface {
  platformFee: number;
  platformFeeType: string;
  sessionFee: number;
  sessionFeeType: string;
  currencyType?: string;
  vat?: number | string;
}

export type { EstimateApiResponse };

interface PreChargeConfigProperties {
  connectors: ConnectorInterface[];
  selectedConnectorId: string | number;
  paymentDetails: any;
  currentPrice: string;
  vat?: number | string;
  loader: boolean;
  onProceedToCharge: (chargeLimit?: ChargeLimitConfig) => void;
  navigateToPaymentMethod: (removeQueryParameters?: boolean) => void;
  fees?: FeesInterface | null;
  evseUid?: string;
  isFreeCharging?: boolean;
}

/**
 * PreChargeConfig Component
 * @param properties Component properties
 * @returns JSX Element
 */
function PreChargeConfig(properties: PreChargeConfigProperties): JSX.Element {
  const { t } = useTranslation('charger');
  const {
    connectors,
    selectedConnectorId,
    paymentDetails,
    currentPrice,
    vat,
    loader,
    onProceedToCharge,
    navigateToPaymentMethod,
    fees,
    evseUid: evseUidProperty,
    isFreeCharging: isFreeProperty,
  } = properties;

  const {
    energyPrice: contextEnergyPrice,
    vat: contextVat,
    fees: contextFees,
    isFreeCharging: contextIsFreeCharging,
    isLoading: isTariffLoading,
  } = useTariff();

  const isFreeCharging =
    isFreeProperty ??
    (contextIsFreeCharging ||
      (fees !== null &&
        fees !== undefined &&
        Number(fees.platformFee ?? 0) === 0 &&
        Number(fees.sessionFee ?? 0) === 0));

  const effectivePrice = currentPrice || contextEnergyPrice;
  const effectiveVat =
    vat !== undefined && vat !== null ? vat : (contextVat ?? fees?.vat ?? contextFees?.vat ?? 0);

  const numericPrice = Number(effectivePrice || 0);
  const numericVat = Number(effectiveVat || 0);

  // When tariffs price and vat is 0, charge by amount must be hidden
  const isTariffPriceAndVatZero = numericPrice === 0 && numericVat === 0;
  const isAmountLimitHidden = isFreeCharging || isTariffPriceAndVatZero;

  // ---- Charge Limit & Tabs State ----
  const [chargeLimitEnabled, setChargeLimitEnabled] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<LimitTab>(isAmountLimitHidden ? 'energy' : 'amount');
  const [amountValue, setAmountValue] = useState<string>('25');
  const [energyValue, setEnergyValue] = useState<string>('5');
  const [timeValue, setTimeValue] = useState<string>('60');
  const [batteryValue, setBatteryValue] = useState<string>('80');
  const [currentSocValue, setCurrentSocValue] = useState<string>('');
  const [batteryCapacityValue, setBatteryCapacityValue] = useState<string>('60');

  const progressCurrentFillReference = useRef<HTMLDivElement>(null);
  const progressTargetFillReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const numericCurrent = Number(currentSocValue);
    const numericTarget = Number(batteryValue);
    if (progressCurrentFillReference.current) {
      const currentPercent = Math.min(
        100,
        Math.max(0, Number.isNaN(numericCurrent) ? 0 : numericCurrent),
      );
      progressCurrentFillReference.current.style.width = `${currentPercent}%`;
    }
    if (progressTargetFillReference.current) {
      const currentPercent = Math.min(
        100,
        Math.max(0, Number.isNaN(numericCurrent) ? 0 : numericCurrent),
      );
      const targetPercent = Math.min(
        100,
        Math.max(0, Number.isNaN(numericTarget) ? 80 : numericTarget),
      );
      const deltaPercent = Math.max(0, targetPercent - currentPercent);
      progressTargetFillReference.current.style.left = `${currentPercent}%`;
      progressTargetFillReference.current.style.width = `${deltaPercent}%`;
    }
  }, [currentSocValue, batteryValue]);

  const initialTabSynchronizedReference = useRef<boolean>(false);
  const previousIsAmountHiddenReference = useRef<boolean>(isAmountLimitHidden);

  useEffect(() => {
    // Hold tab initialization until tariff response resolves
    if (isTariffLoading) {
      return;
    }

    if (!initialTabSynchronizedReference.current) {
      initialTabSynchronizedReference.current = true;
      setActiveTab(isAmountLimitHidden ? 'energy' : 'amount');
      previousIsAmountHiddenReference.current = isAmountLimitHidden;
      return;
    }

    // If previously amount was hidden (0 tariff) and now tariff has positive value,
    // automatically switch to 'amount' so user does not need to manually change tabs
    if (previousIsAmountHiddenReference.current && !isAmountLimitHidden) {
      setActiveTab('amount');
    } else if (isAmountLimitHidden && activeTab === 'amount') {
      setActiveTab('energy');
    }

    previousIsAmountHiddenReference.current = isAmountLimitHidden;
  }, [isTariffLoading, isAmountLimitHidden, activeTab]);

  // ---- Estimate API State ----
  const [estimateData, setEstimateData] = useState<EstimateApiResponse | null>(null);
  const [isEstimating, setIsEstimating] = useState<boolean>(false);
  const [estimateError, setEstimateError] = useState<boolean>(false);
  const estimateRequestIdReference = useRef<number>(0);

  // ---- Bottom Sheets & Keypad State ----
  const [showPriceBreakdown, setShowPriceBreakdown] = useState<boolean>(false);
  const [showKeypad, setShowKeypad] = useState<boolean>(false);
  const [keypadTarget, setKeypadTarget] = useState<KeypadTarget>('amount');
  const [keypadInput, setKeypadInput] = useState<string>('');
  const [isNavigatingToPayment, setIsNavigatingToPayment] = useState<boolean>(false);

  const currentConnector =
    connectors.find((connector) => connector.connectorId === selectedConnectorId) || connectors[0];

  const isDcCharger = Boolean(
    currentConnector?.connector?.toLowerCase().includes('dc') ||
      currentConnector?.currentType?.toUpperCase() === 'DC' ||
      currentConnector?.currentForm?.toUpperCase() === 'DC',
  );

  const effectiveEvseUid =
    evseUidProperty ||
    (currentConnector as any)?.evseUid ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('deviceId') || ''
      : '');

  const effectiveConnectorId = String(currentConnector?.connectorId || selectedConnectorId || '1');

  const platformFee =
    fees?.platformFee !== undefined && fees?.platformFee !== null ? Number(fees.platformFee) : null;
  const sessionFee =
    fees?.sessionFee !== undefined && fees?.sessionFee !== null ? Number(fees.sessionFee) : null;
  const defaultSessionFeeType = fees?.sessionFeeType || 'percentage';
  const defaultPlatformFeeType = fees?.platformFeeType || 'percentage';
  const unitRate = Number(currentPrice || 0);

  const effectivePlatformFee = platformFee ?? 0;
  const effectiveSessionFee = sessionFee ?? 0;

  // Validation for Percentage / Battery Tab
  const numericCurrentSoc = Number(currentSocValue);
  const numericTargetSoc = Number(batteryValue);
  const isSocInvalid =
    activeTab === 'battery' &&
    currentSocValue !== '' &&
    !Number.isNaN(numericCurrentSoc) &&
    !Number.isNaN(numericTargetSoc) &&
    numericCurrentSoc >= numericTargetSoc;

  // Check whether current active mode qualifies for estimate API call
  const isCalculatedTab =
    !isTariffLoading &&
    !isFreeCharging &&
    chargeLimitEnabled &&
    ((activeTab === 'amount' && !isAmountLimitHidden && numericPrice > 0) ||
      activeTab === 'energy' ||
      activeTab === 'time' ||
      (activeTab === 'battery' && isDcCharger && !isSocInvalid));

  const hasEstimateError = isCalculatedTab && estimateError;

  /**
   * Fetches estimate data from the API whenever relevant parameters change
   */
  useEffect(() => {
    let active = true;
    estimateRequestIdReference.current += 1;
    const currentRequestId = estimateRequestIdReference.current;

    if (!isCalculatedTab || !effectiveEvseUid || isFreeCharging) {
      setIsEstimating(false);
      setEstimateData(null);
      setEstimateError(false);
      return undefined;
    }

    setIsEstimating(true);
    setEstimateData(null);
    setEstimateError(false);

    let chargeType: string = activeTab;
    let enteredValue: string | number = '25';
    let currentSoc: number | undefined;
    let batteryCapacityKwh: number | undefined;

    if (activeTab === 'amount') {
      enteredValue = String(amountValue || '25');
    } else if (activeTab === 'energy') {
      enteredValue = String(energyValue || '5');
    } else if (activeTab === 'time') {
      chargeType = 'time';
      enteredValue = Number(timeValue || '60');
    } else if (activeTab === 'battery') {
      chargeType = 'percentage';
      enteredValue = Number(batteryValue || '80');
      currentSoc = Number(currentSocValue || '20');
      batteryCapacityKwh = Number(batteryCapacityValue || '60');
    }

    const debounceTimer = setTimeout(async () => {
      try {
        const estimatePayload: EstimateInterface = {
          evseUid: effectiveEvseUid,
          connectorId: effectiveConnectorId,
          chargeType,
          enteredValue,
          ...(currentSoc !== undefined && !Number.isNaN(currentSoc) ? { currentSoc } : {}),
          ...(batteryCapacityKwh !== undefined && !Number.isNaN(batteryCapacityKwh)
            ? { batteryCapacityKwh }
            : {}),
        };
        const response = await charger.getAdhocEstimate(estimatePayload);

        if (!active || estimateRequestIdReference.current !== currentRequestId) {
          return;
        }

        const data = (response?.data as any)?.data || response?.data;
        if (
          data &&
          (typeof data.estimated_total === 'number' || typeof data.hold_amount === 'number')
        ) {
          setEstimateData(data);
          setEstimateError(false);
        } else {
          setEstimateData(null);
          setEstimateError(true);
        }
      } catch (error) {
        console.error('Failed to fetch adhoc estimate:', error);
        if (active && estimateRequestIdReference.current === currentRequestId) {
          setEstimateData(null);
          setEstimateError(true);
        }
      } finally {
        if (active && estimateRequestIdReference.current === currentRequestId) {
          setIsEstimating(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(debounceTimer);
    };
  }, [
    isCalculatedTab,
    activeTab,
    amountValue,
    energyValue,
    timeValue,
    batteryValue,
    currentSocValue,
    batteryCapacityValue,
    effectiveEvseUid,
    effectiveConnectorId,
  ]);

  /**
   * Calculates pricing and breakdown values dynamically for the active tab and selected amount
   * @returns Breakdown calculation values
   */
  const calculateFees = () => {
    let exampleCharge = 100.0;
    let baseEnergyCost = 100.0;

    if (activeTab === 'amount') {
      baseEnergyCost = Number(amountValue) || 25.0;
      exampleCharge = baseEnergyCost;
    } else if (activeTab === 'energy') {
      baseEnergyCost = (Number(energyValue) || 5) * unitRate;
      exampleCharge = baseEnergyCost;
    } else if (activeTab === 'time' || activeTab === 'battery') {
      baseEnergyCost = 100.0;
      exampleCharge = 100.0;
    }

    const calculatedPlatformFee =
      defaultPlatformFeeType === 'percentage'
        ? Number((baseEnergyCost * (effectivePlatformFee / 100)).toFixed(2))
        : Number(effectivePlatformFee.toFixed(2));

    const calculatedServiceFee =
      defaultSessionFeeType === 'percentage'
        ? Number(
            ((baseEnergyCost + calculatedPlatformFee) * (effectiveSessionFee / 100)).toFixed(2),
          )
        : Number(effectiveSessionFee.toFixed(2));

    const estimatedTotal = Number(
      (baseEnergyCost + calculatedPlatformFee + calculatedServiceFee).toFixed(2),
    );
    const holdAmount = Number((estimatedTotal * 1.1).toFixed(2));

    return {
      exampleCharge,
      baseEnergyCost,
      platformFee: calculatedPlatformFee,
      platformFeeValue: effectivePlatformFee,
      platformFeeType: defaultPlatformFeeType,
      serviceFee: calculatedServiceFee,
      sessionFeeValue: effectiveSessionFee,
      sessionFeeType: defaultSessionFeeType,
      estimatedTotal,
      holdAmount,
    };
  };

  const localFees = calculateFees();

  const currencySymbol = estimateData?.billing_currency || fees?.currencyType || 'SAR';

  const platformFeeValue =
    estimateData?.fees?.platformFee !== undefined
      ? Number(estimateData.fees.platformFee)
      : localFees.platformFeeValue;

  const platformFeeType = estimateData?.fees?.platformFeeType || localFees.platformFeeType;

  const sessionFeeValue =
    estimateData?.fees?.paymentSurcharge !== undefined
      ? Number(estimateData.fees.paymentSurcharge)
      : localFees.sessionFeeValue;

  const sessionFeeType = estimateData?.fees?.paymentSurchargeType || localFees.sessionFeeType;

  /**
   * Opens custom numeric keypad for a specific target parameter
   * @param target Specific target field to edit
   */
  const openKeypad = (target: KeypadTarget) => {
    setKeypadTarget(target);
    let currentValue = '25';
    if (target === 'amount') {
      currentValue = amountValue;
    } else if (target === 'energy') {
      currentValue = energyValue;
    } else if (target === 'time') {
      currentValue = timeValue;
    } else if (target === 'battery') {
      currentValue = batteryValue;
    } else if (target === 'currentSoc') {
      currentValue = currentSocValue;
    } else if (target === 'batteryCapacity') {
      currentValue = batteryCapacityValue;
    }
    setKeypadInput(currentValue);
    setShowKeypad(true);
  };

  /**
   * Handles numeric keypad key presses
   * @param key Pressed key character
   */
  const handleKeypadPress = (key: string) => {
    if (key === 'backspace') {
      setKeypadInput((previous) => previous.slice(0, -1));
      return;
    }

    if (key === '.') {
      if (keypadTarget === 'battery' || keypadTarget === 'currentSoc' || keypadTarget === 'time') {
        return;
      }
      if (!keypadInput.includes('.')) {
        setKeypadInput((previous) => (previous ? `${previous}.` : '0.'));
      }
      return;
    }

    // Battery limit: 1 - 100 %
    if (keypadTarget === 'battery') {
      const nextValue = keypadInput === '0' ? key : `${keypadInput}${key}`;
      const number = Number(nextValue);
      if (number > 100) {
        setKeypadInput('100');
        return;
      }
      if (nextValue === '0') {
        return;
      }
      setKeypadInput(nextValue);
      return;
    }

    // Current SOC limit: 0 - 99 %
    if (keypadTarget === 'currentSoc') {
      const nextValue = keypadInput === '0' ? key : `${keypadInput}${key}`;
      const number = Number(nextValue);
      if (number > 99) {
        setKeypadInput('99');
        return;
      }
      setKeypadInput(nextValue);
      return;
    }

    // Battery Capacity limit (kWh): max 3 integer digits, 1 decimal place
    if (keypadTarget === 'batteryCapacity') {
      if (keypadInput.includes('.')) {
        const [, decimalPart = ''] = keypadInput.split('.');
        if (decimalPart.length >= 1) {
          return;
        }
        setKeypadInput((previous) => `${previous}${key}`);
      } else if (keypadInput === '0') {
        setKeypadInput(key);
      } else if (keypadInput.length < 3) {
        setKeypadInput((previous) => `${previous}${key}`);
      }
      return;
    }

    // Amount limit: 4 integer digits and 2 decimal places (e.g., 2000.00)
    if (keypadTarget === 'amount') {
      if (keypadInput.includes('.')) {
        const [, decimalPart = ''] = keypadInput.split('.');
        if (decimalPart.length >= 2) {
          return;
        }
        setKeypadInput((previous) => `${previous}${key}`);
      } else if (keypadInput === '0') {
        setKeypadInput(key);
      } else if (keypadInput.length < 4) {
        setKeypadInput((previous) => `${previous}${key}`);
      }
      return;
    }

    // Energy limit: 3 integer digits and 2 decimal places
    if (keypadTarget === 'energy') {
      if (keypadInput.includes('.')) {
        const [, decimalPart = ''] = keypadInput.split('.');
        if (decimalPart.length >= 2) {
          return;
        }
        setKeypadInput((previous) => `${previous}${key}`);
      } else if (keypadInput === '0') {
        setKeypadInput(key);
      } else if (keypadInput.length < 3) {
        setKeypadInput((previous) => `${previous}${key}`);
      }
      return;
    }

    // Time limit: 3 integer digits
    if (keypadTarget === 'time') {
      if (keypadInput === '0') {
        setKeypadInput(key);
      } else if (keypadInput.length < 3) {
        setKeypadInput((previous) => `${previous}${key}`);
      }
      return;
    }

    setKeypadInput((previous) => (previous === '0' ? key : `${previous}${key}`));
  };

  /**
   * Confirms custom keypad input and updates target value
   */
  const handleKeypadDone = () => {
    let value = keypadInput || '0';
    if (value.endsWith('.')) {
      value = value.slice(0, -1);
    }

    if (keypadTarget === 'amount') {
      const parsedAmount = Number(value);
      if (!value || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
        setAmountValue('25');
      } else {
        setAmountValue(value);
      }
    } else if (keypadTarget === 'energy') {
      const parsedEnergy = Number(value);
      if (!value || Number.isNaN(parsedEnergy) || parsedEnergy <= 0) {
        setEnergyValue('5');
      } else {
        setEnergyValue(value);
      }
    } else if (keypadTarget === 'time') {
      const parsedTime = Number(value);
      if (!value || Number.isNaN(parsedTime) || parsedTime <= 0) {
        setTimeValue('60');
      } else {
        setTimeValue(value);
      }
    } else if (keypadTarget === 'battery') {
      const parsedBattery = Number(value);
      let clampedBattery = '80';
      if (!parsedBattery || parsedBattery < 1) {
        clampedBattery = '1';
      } else if (parsedBattery > 100) {
        clampedBattery = '100';
      } else {
        clampedBattery = String(Math.round(parsedBattery));
      }
      setBatteryValue(clampedBattery);
    } else if (keypadTarget === 'currentSoc') {
      if (!keypadInput) {
        setCurrentSocValue('');
      } else {
        const parsedSoc = Number(keypadInput);
        let clampedSoc = '20';
        if (Number.isNaN(parsedSoc) || parsedSoc < 0) {
          clampedSoc = '0';
        } else if (parsedSoc > 99) {
          clampedSoc = '99';
        } else {
          clampedSoc = String(Math.round(parsedSoc));
        }
        setCurrentSocValue(clampedSoc);
      }
    } else if (keypadTarget === 'batteryCapacity') {
      const parsedCap = Number(value);
      if (!value || Number.isNaN(parsedCap) || parsedCap <= 0) {
        setBatteryCapacityValue('60');
      } else {
        setBatteryCapacityValue(value);
      }
    }
    setShowKeypad(false);
  };

  /**
   * Gets limit value based on current tab
   * @returns Current active tab limit value
   */
  const getActiveLimitValue = (): string => {
    if (activeTab === 'amount') {
      return amountValue;
    }
    if (activeTab === 'energy') {
      return energyValue;
    }
    if (activeTab === 'time') {
      return timeValue;
    }
    const value = Math.min(100, Math.max(1, Math.round(Number(batteryValue) || 80)));
    return String(value);
  };

  /**
   * Handles proceed to charge button click
   */
  const handleProceed = () => {
    onProceedToCharge(
      chargeLimitEnabled
        ? {
            type: activeTab,
            value: getActiveLimitValue(),
            ...(activeTab === 'battery'
              ? {
                  currentSoc: currentSocValue,
                  batteryCapacityKwh: batteryCapacityValue,
                }
              : {}),
          }
        : undefined,
    );
  };

  /**
   * Gets unit label for keypad title
   * @returns Unit title string
   */
  const getKeypadTitleUnit = (): string => {
    if (keypadTarget === 'amount') {
      return t('limitAmount');
    }
    if (keypadTarget === 'energy') {
      return t('limitEnergy');
    }
    if (keypadTarget === 'time') {
      return t('limitTime');
    }
    if (keypadTarget === 'currentSoc') {
      return t('yourBatteryNow');
    }
    if (keypadTarget === 'batteryCapacity') {
      return `${t('batteryCapacity')} (kWh)`;
    }
    return `${t('limitBattery')} (1 - 100%)`;
  };

  /**
   * Gets unit label for keypad display
   * @returns Unit symbol string
   */
  const getKeypadDisplayUnit = (): string => {
    if (keypadTarget === 'amount') {
      return currencySymbol;
    }
    if (keypadTarget === 'energy' || keypadTarget === 'batteryCapacity') {
      return 'kWh';
    }
    if (keypadTarget === 'time') {
      return 'Mins';
    }
    return '%';
  };

  const savedCardDetails = getLocalStorageItem('paymentCardDetails');
  const storedPaymentMethod = getLocalStorageItem('paymentMethod');

  const cardBrand = (
    paymentDetails?.brand ||
    savedCardDetails?.brand ||
    getLocalStorageItem('paymentBrand') ||
    ''
  ).toUpperCase();

  const cardLast4 =
    paymentDetails?.last4 || savedCardDetails?.last4 || getLocalStorageItem('paymentLast4') || '';

  let cardTitle = t('credit_debit_card');
  if (cardBrand && cardLast4) {
    cardTitle = `${cardBrand} •••• ${cardLast4}`;
  } else if (cardLast4) {
    cardTitle = `Card •••• ${cardLast4}`;
  } else if (cardBrand) {
    cardTitle = cardBrand;
  } else if (storedPaymentMethod === 'applepay_via_hyperpay') {
    cardTitle = t('apple_pay');
  }

  /**
   * Computes display value for estimated total
   * @returns Formatted total JSX, string, or null
   */
  const renderEstimatedTotalDisplay = () => {
    if (isFreeCharging) {
      return '0.00';
    }
    if (isEstimating) {
      return <span className="estimate-loading">...</span>;
    }
    if (hasEstimateError) {
      return null;
    }
    if (estimateData?.estimated_total !== undefined && estimateData?.estimated_total !== null) {
      return Number(estimateData.estimated_total).toFixed(2);
    }
    return null;
  };

  /**
   * Computes display value for payment hold amount
   * @returns Formatted hold amount string
   */
  const getHoldAmountDisplay = (): string => {
    if (isFreeCharging) {
      return '0.00';
    }
    if (isEstimating) {
      return '...';
    }
    if (estimateData?.hold_amount !== undefined && estimateData?.hold_amount !== null) {
      return Number(estimateData.hold_amount).toFixed(2);
    }
    return Number(localFees.holdAmount ?? 25).toFixed(2);
  };

  /**
   * Computes breakdown estimated total string
   * @returns Formatted total string
   */
  const getBreakdownEstimatedTotal = (): string => {
    if (isFreeCharging) {
      return '0.00';
    }
    if (isCalculatedTab && isEstimating) {
      return '...';
    }
    if (hasEstimateError) {
      return '0.00';
    }
    if (estimateData?.estimated_total !== undefined && estimateData?.estimated_total !== null) {
      return Number(estimateData.estimated_total).toFixed(2);
    }
    return Number(localFees.estimatedTotal ?? 0).toFixed(2);
  };

  return (
    <div className="precharge-config-page">
      {/* Scrollable Content */}
      <div className="precharge-scrollable-content">
        {/* Top Card: Connector & Payment Mode Info */}
        <div className="precharge-card connector-payment-card">
          {/* Connector Row */}
          <div className="info-row">
            <div className="info-icon-box">
              <PlugIcon className="info-svg-icon" />
            </div>
            <div className="info-text-box">
              <h3 className="info-title">
                {currentConnector?.connector || `Connector #${selectedConnectorId || 1}`} ·
              </h3>
              <p className="info-subtitle">{currentConnector?.capacity || '1 kWh'}</p>
            </div>
            <div className="info-badge-status">
              <span className="dot" />
              <span>{currentConnector?.status || t('available')}</span>
            </div>
          </div>

          <div className="card-divider" />

          {/* Payment Row */}
          <div className="info-row">
            <div className="info-icon-box">
              <svg
                className="info-svg-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="3" ry="3" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
            </div>
            <div className="info-text-box">
              <h3 className="info-title">
                {isFreeCharging ? t('freeCharging') || 'Free Charging' : cardTitle}
              </h3>
              <p className="info-subtitle">
                {isFreeCharging
                  ? t('noPaymentRequired') || 'No payment method required'
                  : t('default')}
              </p>
            </div>
            {!isFreeCharging && (
              <button
                type="button"
                className="change-link-btn"
                disabled={isNavigatingToPayment}
                aria-busy={isNavigatingToPayment}
                onClick={async () => {
                  if (isNavigatingToPayment) return;
                  setIsNavigatingToPayment(true);
                  try {
                    await navigateToPaymentMethod(true);
                  } finally {
                    setIsNavigatingToPayment(false);
                  }
                }}>
                {isNavigatingToPayment ? '...' : `${t('change')} >`}
              </button>
            )}
          </div>
        </div>

        {/* Dynamic Tariff & Fees Overview */}
        <TariffFeesOverview />

        {/* Middle Card: Charge Limit with 4 Toggles */}
        <div className="precharge-card charge-limit-card">
          <div className="charge-limit-header">
            <div className="header-left">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="limit-icon">
                <circle cx="12" cy="12" r="10" />
                <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
              </svg>
              <span className="title">{t('chargeLimit')}</span>
            </div>
            <div className="header-right">
              <label className="toggle-switch" htmlFor="charge-limit-toggle">
                <span className="sr-only">{t('toggleChargeLimit')}</span>
                <input
                  id="charge-limit-toggle"
                  type="checkbox"
                  checked={chargeLimitEnabled}
                  onChange={(event_) => setChargeLimitEnabled(event_.target.checked)}
                />
                <span className="slider round" />
              </label>
              <button
                type="button"
                className="info-circle-btn"
                aria-label={t('priceBreakdownInfo')}
                disabled={hasEstimateError}
                onClick={() => !hasEstimateError && setShowPriceBreakdown(true)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </button>
            </div>
          </div>

          {chargeLimitEnabled ? (
            <div className="charge-limit-body">
              {/* 4 Segmented Toggles: Amount, Energy, Time, Battery */}
              <div className="limit-tabs-segmented">
                {!isAmountLimitHidden && (
                  <button
                    type="button"
                    className={`tab-btn ${activeTab === 'amount' ? 'active' : ''}`}
                    onClick={() => setActiveTab('amount')}>
                    {t('limitAmount')}
                  </button>
                )}
                <button
                  type="button"
                  className={`tab-btn ${activeTab === 'energy' ? 'active' : ''}`}
                  onClick={() => setActiveTab('energy')}>
                  {t('limitEnergy')}
                </button>
                <button
                  type="button"
                  className={`tab-btn ${activeTab === 'time' ? 'active' : ''}`}
                  onClick={() => setActiveTab('time')}>
                  {t('limitTime')}
                </button>
                <button
                  type="button"
                  className={`tab-btn ${activeTab === 'battery' ? 'active' : ''}`}
                  onClick={() => setActiveTab('battery')}>
                  {t('limitBattery')}
                </button>
              </div>

              {/* If Battery Tab is active on a non-DC charger, show Not Applicable Notice */}
              {activeTab === 'battery' && !isDcCharger ? (
                <div className="not-applicable-box">
                  <div className="info-icon-circle">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  <h4 className="not-applicable-title">{t('notApplicableForCharger')}</h4>
                  <p className="not-applicable-desc">{t('notApplicableDesc')}</p>
                </div>
              ) : (
                <>
                  {/* Single Display Button for Amount, Energy, Time, Battery */}
                  <button
                    type="button"
                    className="tab-content-display-btn"
                    onClick={() => openKeypad(activeTab)}>
                    {activeTab === 'amount' && (
                      <>
                        <div className="value-display">
                          <span className="unit">{currencySymbol}</span>
                          <span className={`number ${amountValue ? '' : 'placeholder'}`}>
                            {amountValue || 'Set'}
                          </span>
                        </div>
                        <p className="tap-subtext">{t('tapToEnterValue')}</p>
                        <p className="helper-text">{t('stopAtAboutThis')}</p>
                      </>
                    )}
                    {activeTab === 'energy' && (
                      <>
                        <div className="value-display">
                          <span className={`number ${energyValue ? '' : 'placeholder'}`}>
                            {energyValue || 'Set'}
                          </span>
                          <span className="unit">kWh</span>
                        </div>
                        <p className="tap-subtext">{t('tapToEnterValue')}</p>
                        <p className="helper-text">{t('stopAtAboutThis')}</p>
                      </>
                    )}
                    {activeTab === 'time' && (
                      <>
                        <div className="value-display">
                          <span className={`number ${timeValue ? '' : 'placeholder'}`}>
                            {timeValue || 'Set'}
                          </span>
                          <span className="unit">Mins</span>
                        </div>
                        <p className="tap-subtext">{t('tapToEnterValue')}</p>
                        <p className="helper-text">{t('stopAtAboutThis')}</p>
                      </>
                    )}
                    {activeTab === 'battery' && (
                      <>
                        <div className="value-display">
                          <span className={`number ${batteryValue ? '' : 'placeholder'}`}>
                            {batteryValue || 'Set'}
                          </span>
                          <span className="unit">%</span>
                        </div>
                        <p className="tap-subtext">{t('tapToEnterValue')}</p>
                        <p className="helper-text">{t('stopAtAboutThis')}</p>
                      </>
                    )}
                  </button>

                  {/* Battery / Percentage Charging Control */}
                  {activeTab === 'battery' && isDcCharger && (
                    <div className="battery-config-container">
                      {/* Target SOC Quick Pills */}
                      <div className="quick-select-pills">
                        {['50', '80', '90'].map((value) => (
                          <button
                            key={value}
                            type="button"
                            className={`pill-btn ${batteryValue === value ? 'selected' : ''}`}
                            onClick={() => setBatteryValue(value)}>
                            {value}%
                          </button>
                        ))}
                        <button
                          type="button"
                          className={`pill-btn ${!['50', '80', '90'].includes(batteryValue) ? 'selected' : ''}`}
                          onClick={() => openKeypad('battery')}>
                          {t('custom')}
                        </button>
                      </div>

                      {/* Current Battery Section */}
                      <div className="current-battery-section">
                        {!currentSocValue ? (
                          <div className="current-soc-prompt-wrap">
                            <button
                              type="button"
                              className="set-current-soc-btn"
                              onClick={() => openKeypad('currentSoc')}>
                              <svg
                                className="battery-icon"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <rect x="1" y="6" width="18" height="12" rx="2" ry="2" />
                                <line x1="23" y1="11" x2="23" y2="13" />
                                <line x1="5" y1="10" x2="5" y2="14" />
                              </svg>
                              <span>{t('setCurrentBattery')}</span>
                            </button>
                          </div>
                        ) : (
                          <div className="current-soc-progress-row">
                            <button
                              type="button"
                              className="current-soc-pill-btn"
                              onClick={() => openKeypad('currentSoc')}>
                              <svg
                                className="battery-icon"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <rect x="1" y="6" width="18" height="12" rx="2" ry="2" />
                                <line x1="23" y1="11" x2="23" y2="13" />
                                <line x1="5" y1="10" x2="5" y2="14" />
                              </svg>
                              <span className="soc-value-text">
                                {currentSocValue ? `${currentSocValue}%` : '--%'}
                              </span>
                              <svg
                                className="edit-icon"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                              </svg>
                            </button>

                            <div className="soc-progress-indicator-wrap">
                              <div className="soc-progress-track">
                                <div
                                  ref={progressCurrentFillReference}
                                  className="soc-progress-current-fill"
                                />
                                <div
                                  ref={progressTargetFillReference}
                                  className="soc-progress-target-fill"
                                />
                              </div>
                              <span className="soc-gain-text">
                                +
                                {Math.max(
                                  0,
                                  (Number(batteryValue) || 80) - (Number(currentSocValue) || 0),
                                )}
                                %
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Inline Validation Error Banner */}
                      {isSocInvalid && (
                        <div className="soc-validation-error">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                          </svg>
                          <span>{t('currentSocMustBeBelowTarget')}</span>
                        </div>
                      )}

                      {/* Adhoc Battery Capacity Selector */}
                      <div className="battery-capacity-section">
                        <div className="capacity-header">
                          <span className="capacity-title">{t('batteryCapacity')}</span>
                          <span className="capacity-badge">{batteryCapacityValue || '60'} kWh</span>
                        </div>
                        <div className="quick-select-pills capacity-pills">
                          {['40', '60', '75', '100'].map((cap) => (
                            <button
                              key={cap}
                              type="button"
                              className={`pill-btn ${batteryCapacityValue === cap ? 'selected' : ''}`}
                              onClick={() => setBatteryCapacityValue(cap)}>
                              {cap} kWh
                            </button>
                          ))}
                          <button
                            type="button"
                            className={`pill-btn ${!['40', '60', '75', '100'].includes(batteryCapacityValue) ? 'selected' : ''}`}
                            onClick={() => openKeypad('batteryCapacity')}>
                            {t('custom')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick Select Buttons & Custom for Amount, Energy, Time */}
                  {activeTab !== 'battery' && (
                    <div className="quick-select-pills">
                      {activeTab === 'amount' && (
                        <>
                          {['25', '50', '100'].map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={`pill-btn ${amountValue === value ? 'selected' : ''}`}
                              onClick={() => setAmountValue(value)}>
                              {value}
                            </button>
                          ))}
                          <button
                            type="button"
                            className={`pill-btn ${!['25', '50', '100'].includes(amountValue) ? 'selected' : ''}`}
                            onClick={() => openKeypad('amount')}>
                            {t('custom')}
                          </button>
                        </>
                      )}
                      {activeTab === 'energy' && (
                        <>
                          {['5', '10', '20'].map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={`pill-btn ${energyValue === value ? 'selected' : ''}`}
                              onClick={() => setEnergyValue(value)}>
                              {value}
                            </button>
                          ))}
                          <button
                            type="button"
                            className={`pill-btn ${!['5', '10', '20'].includes(energyValue) ? 'selected' : ''}`}
                            onClick={() => openKeypad('energy')}>
                            {t('custom')}
                          </button>
                        </>
                      )}
                      {activeTab === 'time' && (
                        <>
                          {['30', '60', '90'].map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={`pill-btn ${timeValue === value ? 'selected' : ''}`}
                              onClick={() => setTimeValue(value)}>
                              {value}
                            </button>
                          ))}
                          <button
                            type="button"
                            className={`pill-btn ${!['30', '60', '90'].includes(timeValue) ? 'selected' : ''}`}
                            onClick={() => openKeypad('time')}>
                            {t('custom')}
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* Stop Condition Notice when delivered kWh is estimated (shown only for Amount and Energy) */}
                  {(activeTab === 'amount' || activeTab === 'energy') &&
                    estimateData?.ordered_kwh && (
                      <div className="stopping-condition-notice">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                        </svg>
                        <span>
                          {t('chargingMayStopAtKwh', {
                            kwh: Number(estimateData.ordered_kwh).toFixed(1),
                          })}
                        </span>
                      </div>
                    )}
                </>
              )}
            </div>
          ) : (
            <div className="charge-unlimited-body">
              <div className="unlimited-icon">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <h4 className="unlimited-title">{t('chargeUntilYouStop')}</h4>
              <p className="unlimited-desc">{t('chargeUntilYouStopDesc')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Bar: Estimated Total & Proceed Button */}
      <div className="precharge-bottom-bar">
        <div className="estimated-total-row">
          {hasEstimateError ? (
            <div className="estimate-error-message">
              <span>{t('unableToCalculateEstimate')}</span>
            </div>
          ) : (
            <>
              <div className="total-left">
                <button
                  type="button"
                  className="label-with-info-btn"
                  onClick={() => setShowPriceBreakdown(true)}>
                  <span>
                    {isFreeCharging || renderEstimatedTotalDisplay() !== null
                      ? t('estimatedTotal')
                      : t('paymentHold')}
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </button>
                {isFreeCharging ? (
                  <p className="hold-subtext">
                    {t('freeSessionNoHold') || 'No payment hold required'}
                  </p>
                ) : (
                  <p className="hold-subtext">
                    {t('heldReleasedAfter', {
                      currency: currencySymbol,
                      amount: getHoldAmountDisplay(),
                    })}
                  </p>
                )}
              </div>
              <div className="total-right">
                {renderEstimatedTotalDisplay() !== null ? (
                  <span className="total-amount">
                    {currencySymbol} {renderEstimatedTotalDisplay()}
                  </span>
                ) : (
                  <span className="total-amount hold-only">
                    {currencySymbol} {getHoldAmountDisplay()}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <button
          type="button"
          className="proceed-charge-button"
          disabled={loader || isEstimating || isSocInvalid}
          onClick={handleProceed}>
          {loader ? (
            <span>{t('loading')}</span>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
              <span>{t('proceedToCharge')}</span>
            </>
          )}
        </button>
      </div>

      {/* Price Breakdown Modal ("How pricing works") */}
      {showPriceBreakdown && (
        <div
          className="bottom-sheet-overlay"
          role="presentation"
          onClick={() => setShowPriceBreakdown(false)}>
          <div
            className="bottom-sheet-content"
            role="presentation"
            onClick={(event_) => event_.stopPropagation()}>
            <div className="sheet-drag-handle" />
            <h2 className="sheet-title">{t('howPricingWorks')}</h2>

            {hasEstimateError ? (
              <>
                <div className="estimate-info-breakdown-card">
                  <div className="estimate-info-icon-wrapper">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="info-icon">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  <div className="estimate-info-message-content">
                    <h3 className="estimate-info-title">
                      {t('unableToCalculateEstimate') ||
                        'Currently unable to calculate estimate value'}
                    </h3>
                    <p className="estimate-info-desc">{t('feesAppliedWhenSessionEnds')}</p>
                  </div>
                </div>

                <MyButton
                  label={t('done') || 'Done'}
                  buttonType="submit"
                  onClickFunc={() => setShowPriceBreakdown(false)}
                />
              </>
            ) : (
              <>
                <p className="sheet-subtitle">
                  {t('everySessionAdds', {
                    platformFee:
                      (estimateData?.fees?.platformFeeType || platformFeeType) === 'percentage'
                        ? `${estimateData?.fees?.platformFee ?? platformFeeValue ?? 0}%`
                        : `${estimateData?.billing_currency || currencySymbol} ${Number(
                            estimateData?.fees?.platformFee ?? localFees.platformFee ?? 0,
                          ).toFixed(2)}`,
                    serviceFee:
                      (estimateData?.fees?.paymentSurchargeType || sessionFeeType) === 'percentage'
                        ? `${estimateData?.fees?.paymentSurcharge ?? sessionFeeValue ?? 0}%`
                        : `${estimateData?.billing_currency || currencySymbol} ${Number(
                            estimateData?.fees?.paymentSurcharge ?? localFees.serviceFee ?? 0,
                          ).toFixed(2)}`,
                  })}
                </p>

                {typeof estimateData?.estimated_total === 'number' ? (
                  <>
                    <p className="breakdown-section-title">
                      {t('forACharge', {
                        charge: `${estimateData?.billing_currency || currencySymbol} ${Number(
                          estimateData?.entered_value ?? localFees.exampleCharge ?? 0,
                        ).toFixed(2)}`,
                      })}
                    </p>

                    <div className="breakdown-list">
                      <div className="breakdown-row">
                        <span>{t('energy')}</span>
                        <span>
                          {estimateData?.billing_currency || currencySymbol}{' '}
                          {Number(
                            estimateData?.estimated_energy_cost ?? localFees.baseEnergyCost ?? 0,
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-row">
                        <span>
                          {t('platformFee')}{' '}
                          {(estimateData?.fees?.platformFeeType || platformFeeType) === 'percentage'
                            ? `(${estimateData?.fees?.platformFee ?? platformFeeValue ?? 0}%)`
                            : ''}
                        </span>
                        <span>
                          {estimateData?.billing_currency || currencySymbol}{' '}
                          {Number(
                            (estimateData?.fees?.platformFeeType || platformFeeType) ===
                              'percentage'
                              ? (estimateData?.fees?.platformAmount ?? localFees.platformFee ?? 0)
                              : (estimateData?.fees?.platformFee ?? localFees.platformFee ?? 0),
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-row">
                        <span>
                          {t('surchargeFee')}{' '}
                          {(estimateData?.fees?.paymentSurchargeType || sessionFeeType) ===
                          'percentage'
                            ? `(${estimateData?.fees?.paymentSurcharge ?? sessionFeeValue ?? 0}%)`
                            : ''}
                        </span>
                        <span>
                          {estimateData?.fees?.currency ||
                            estimateData?.billing_currency ||
                            currencySymbol}{' '}
                          {Number(
                            estimateData?.fees?.paymentSurchargeAmount ??
                              ((estimateData?.fees?.paymentSurchargeType || sessionFeeType) ===
                              'amount'
                                ? (estimateData?.fees?.paymentSurcharge ??
                                  localFees.serviceFee ??
                                  0)
                                : (localFees.serviceFee ?? 0)),
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-divider" />
                      <div className="breakdown-row total-highlight">
                        <span>{t('estimatedTotal')}</span>
                        <span>
                          {estimateData?.billing_currency || currencySymbol}{' '}
                          {getBreakdownEstimatedTotal()}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="breakdown-list">
                    <div className="breakdown-row total-highlight">
                      <span>{t('paymentHold')}</span>
                      <span>
                        {currencySymbol} {getHoldAmountDisplay()}
                      </span>
                    </div>
                  </div>
                )}

                <div className="breakdown-footer-notes">
                  <p>
                    {t('holdNotice', {
                      amount: `${estimateData?.billing_currency || currencySymbol} ${getHoldAmountDisplay()}`,
                    })}
                  </p>
                  <p>{t('feesAppliedWhenSessionEnds')}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Custom Keypad Modal */}
      {showKeypad && (
        <div
          className="bottom-sheet-overlay"
          role="presentation"
          onClick={() => setShowKeypad(false)}>
          <div
            className="bottom-sheet-content keypad-sheet"
            role="presentation"
            onClick={(event_) => event_.stopPropagation()}>
            <div className="sheet-drag-handle" />
            <h2 className="sheet-title">
              {t('enterKeypadTitle', {
                unit: getKeypadTitleUnit(),
              })}
            </h2>

            <div className="keypad-value-display">
              <span className="value">{keypadInput || '0'}</span>
              <span className="unit">{getKeypadDisplayUnit()}</span>
            </div>

            <div className="numeric-keypad-grid">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'backspace'].map((key) => {
                const isDotDisabled =
                  key === '.' &&
                  (keypadTarget === 'battery' ||
                    keypadTarget === 'currentSoc' ||
                    keypadTarget === 'time');
                return (
                  <button
                    key={key}
                    type="button"
                    className={`keypad-key ${isDotDisabled ? 'disabled-key' : ''}`}
                    disabled={isDotDisabled}
                    onClick={() => handleKeypadPress(key)}>
                    {key === 'backspace' ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                        <line x1="18" y1="9" x2="12" y2="15" />
                        <line x1="12" y1="9" x2="18" y2="15" />
                      </svg>
                    ) : (
                      key
                    )}
                  </button>
                );
              })}
            </div>

            <MyButton label={t('done')} buttonType="submit" onClickFunc={handleKeypadDone} />
          </div>
        </div>
      )}
    </div>
  );
}

export default PreChargeConfig;
